import client from './client'
import type { AdminUser, AdminEventsPage, AdminEventType, AllowedEmail, SessionSummary } from '../types'

// ── User management ─────────────────────────────────────────────────────────

export async function listUsers(): Promise<AdminUser[]> {
  const { data } = await client.get<AdminUser[]>('/api/v1/admin/users')
  return data
}

export async function setUserRole(userId: string, role: string): Promise<AdminUser> {
  const { data } = await client.put<AdminUser>(`/api/v1/admin/users/${userId}/role`, { role })
  return data
}

export async function removeUser(userId: string): Promise<void> {
  await client.delete(`/api/v1/admin/users/${userId}`)
}

/** Force-logout: invalidates all of the user's tokens; they must log in again. */
export async function forceLogoutUser(userId: string): Promise<AdminUser> {
  const { data } = await client.post<AdminUser>(`/api/v1/admin/users/${userId}/logout`)
  return data
}

export async function toggleUserNotifications(userId: string, enabled: boolean): Promise<AdminUser> {
  const { data } = await client.put<AdminUser>(
    `/api/v1/admin/users/${userId}/notifications?enabled=${enabled}`
  )
  return data
}

export async function setAccountEnabled(userId: string, enabled: boolean): Promise<AdminUser> {
  const { data } = await client.put<AdminUser>(
    `/api/v1/admin/users/${userId}/account?enabled=${enabled}`
  )
  return data
}

export async function setTestAlertsEnabled(userId: string, enabled: boolean): Promise<void> {
  await client.put(`/api/v1/admin/users/${userId}/test-alerts?enabled=${enabled}`)
}

export async function setUserLimits(
  userId: string,
  maxPhoneNumbers: number,
  maxNotificationChannels: number
): Promise<AdminUser> {
  const { data } = await client.put<AdminUser>(
    `/api/v1/admin/users/${userId}/limits?maxPhoneNumbers=${maxPhoneNumbers}&maxNotificationChannels=${maxNotificationChannels}`
  )
  return data
}

// ── Events ──────────────────────────────────────────────────────────────────

export async function getAdminEvents(
  type?: AdminEventType | null,
  from?: string | null,
  to?: string | null,
  cursor?: string | null,
  pageSize = 20
): Promise<AdminEventsPage> {
  const params = new URLSearchParams({ pageSize: String(pageSize) })
  if (type) params.set('type', type)
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (cursor) params.set('cursor', cursor)
  const { data } = await client.get<AdminEventsPage>(`/api/v1/admin/events?${params}`)
  return data
}

export async function deleteAdminEvents(eventIds: string[]): Promise<void> {
  await client.delete('/api/v1/admin/events', { data: { eventIds } })
}

// ── Email allowlist ──────────────────────────────────────────────────────────

export async function listAllowedEmails(): Promise<AllowedEmail[]> {
  const { data } = await client.get<AllowedEmail[]>('/api/v1/admin/allowed-emails')
  return data
}

export async function addAllowedEmail(email: string, note?: string): Promise<AllowedEmail> {
  const { data } = await client.post<AllowedEmail>('/api/v1/admin/allowed-emails', { email, note })
  return data
}

export async function updateAllowedEmailNote(email: string, note: string): Promise<AllowedEmail> {
  const { data } = await client.put<AllowedEmail>(
    `/api/v1/admin/allowed-emails/${encodeURIComponent(email)}/note`,
    { note }
  )
  return data
}

export async function removeAllowedEmail(email: string): Promise<void> {
  await client.delete(`/api/v1/admin/allowed-emails/${encodeURIComponent(email)}`)
}

// ── Admin sessions ───────────────────────────────────────────────────────────

export async function getAdminSessionSummary(userId: string, month: string): Promise<SessionSummary> {
  const { data } = await client.get<SessionSummary>(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/summary?month=${month}`
  )
  return data
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Downloads Excel report for any user (admin) via axios (handles auth refresh, binary decoding). */
export async function downloadAdminExcel(userId: string, month: string): Promise<void> {
  const response = await client.get(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/export`,
    {
      params: { month },
      responseType: 'blob',
      headers: { Accept: XLSX_MIME },
    },
  )
  const cd: string = response.headers['content-disposition'] ?? ''
  const filename = cd.match(/filename="([^"]+)"/)?.[1] ?? `on-call-${userId}-${month}.xlsx`
  const blob = new Blob([response.data], { type: XLSX_MIME })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
